/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  forwardRef,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { cssFns } from "@superweb/css";

import { useUiColors } from "../theme";
import { useTypo } from "../typo";
import { useIsMobile } from "../mobile-context";
import { BottomSheetContextProvider } from "../bottom-sheet/bottom-sheet";

const navMenuWidth = 64;
const headerDesktopHeight = 60;
const headerMobileHeight = 56;
const mainPaddings = 16;
const mainPaddingBlockStart = 8;

const useBrowserLayoutEffect =
  // reason for check window.document - https://github.com/adobe/react-spectrum/issues/7926#issuecomment-2721587586
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  typeof window !== "undefined" && window.document ? useLayoutEffect : () => {};

export const AppLayout = ({
  menu,
  content,
  __experimental_app_menu,
}: {
  content: ReactNode;
} & (
  | { menu?: ReactNode; __experimental_app_menu?: never }
  | { menu?: never; __experimental_app_menu?: ReactNode }
)) => {
  const isMobile = useIsMobile();

  if (__experimental_app_menu) {
    return (
      <div
        css={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : `auto 1fr`,
          gridTemplateRows: `minmax(min-content, 100vh) 1fr`,
          minHeight: "100vh",
          isolation: "isolate",
          minWidth: "min-content",
        }}
        // Workaround for set dvh in SSR
        // If browser does not support dvh, then set height to vh in css
        style={{
          minHeight: "100dvh",
          gridTemplateRows: `minmax(min-content, 100dvh) 1fr`,
        }}
      >
        {Boolean(__experimental_app_menu) &&
          (!isMobile ? (
            <div
              css={{
                position: "sticky",
                // Workaround for set dvh in SSR
                // If browser does not support dvh, then set height to vh in css
                height: "100vh",
                insetBlockStart: "0",
                insetInlineStart: "0",
                zIndex: "2",
                display: "grid",
                ...cssFns.gridArea("1"),
              }}
              // Workaround for set dvh in SSR
              // If browser does not support dvh, then set height to vh in css
              style={{
                height: "100dvh",
              }}
            >
              {__experimental_app_menu}
            </div>
          ) : (
            __experimental_app_menu
          ))}
        <div>{content}</div>
      </div>
    );
  }

  return (
    <div
      css={{
        display: "grid",
        // Workaround for set dvh in SSR
        // If browser does not support dvh, then set height to vh in css
        gridTemplateColumns:
          isMobile || !menu ? "1fr" : `${navMenuWidth}px 1fr`,
        gridTemplateRows: `minmax(min-content, 100vh) 1fr`,
        minHeight: "100vh",
        isolation: "isolate",
        minWidth: "min-content",
      }}
      // Workaround for set dvh in SSR
      // If browser does not support dvh, then set height to vh in css
      style={{
        minHeight: "100dvh",
        gridTemplateRows: `minmax(min-content, 100dvh) 1fr`,
      }}
    >
      {!isMobile && menu ? (
        <div
          css={{
            position: "sticky",
            // Workaround for set dvh in SSR
            // If browser does not support dvh, then set height to vh in css
            height: "100vh",
            insetBlockStart: "0",
            insetInlineStart: "0",
            zIndex: "2",
            display: "grid",
            ...cssFns.gridArea("1"),
          }}
          // Workaround for set dvh in SSR
          // If browser does not support dvh, then set height to vh in css
          style={{
            height: "100dvh",
          }}
        >
          {menu}
        </div>
      ) : undefined}
      <div>{content}</div>
    </div>
  );
};

export const PageLayout = ({
  title,
  hiddenTitle,
  burgerMenuButton,
  toolbar,
  userToolbar,
  content,
  stickyHeader = false,
  disableContentMargins = false,
}: {
  burgerMenuButton: ReactNode;
  toolbar?: ReactNode;
  userToolbar?: ReactNode;
  content: ReactNode;
  stickyHeader?: boolean;
  disableContentMargins?: boolean;
} & (
  | { title: ReactNode; hiddenTitle?: never }
  | {
      title?: never;

      /**
       * Gives the page an accessible name.
       * Need to specify when the 'title' is not specified.
       * This prop is required for seo.
       */
      hiddenTitle: string;
    }
)) => {
  const isMobile = useIsMobile();

  const bottomSheetContainerRef = useRef<HTMLDivElement>(null);

  return (
    <BottomSheetContextProvider value={bottomSheetContainerRef}>
      <Header stickyHeader={stickyHeader}>
        <div css={{ display: "flex", alignItems: "center", flexGrow: "1" }}>
          {isMobile && (
            <div
              css={{
                width: "56px",
                height: `${headerMobileHeight}px`,
                display: "grid",
                alignItems: "center",
                justifyItems: "center",
              }}
            >
              {burgerMenuButton}
            </div>
          )}
          <Title {...(hiddenTitle ? { hiddenTitle } : { title })} />
          <div css={{ display: "flex", flexGrow: "1", marginInlineEnd: "8px" }}>
            {toolbar}
          </div>
        </div>
        {userToolbar ? <div>{userToolbar}</div> : undefined}
      </Header>

      <main
        css={{
          display: "grid",
          // Workaround for set dvh in SSR
          // If browser does not support dvh, then set height to vh in css
          minHeight: `calc(100vh - ${
            isMobile ? headerMobileHeight : headerDesktopHeight
          }px)`,
          boxSizing: "border-box",
          ...(!isMobile &&
            !disableContentMargins && {
              ...cssFns.padding(`${mainPaddings}px`),
              paddingBlockStart: `${mainPaddingBlockStart}px`,
            }),
        }}
        // Workaround for set dvh in SSR
        // If browser does not support dvh, then set height to vh in css
        style={{
          minHeight: `calc(100dvh - ${
            isMobile ? headerMobileHeight : headerDesktopHeight
          }px)`,
        }}
      >
        {content}
      </main>
      <BottomSheetPortal ref={bottomSheetContainerRef} />
    </BottomSheetContextProvider>
  );
};

const Header = ({
  stickyHeader,
  children,
}: {
  stickyHeader: boolean;
  children: ReactNode;
}) => {
  const uiColors = useUiColors();
  const [scrollWidth, setScrollWidth] = useState(0);

  useBrowserLayoutEffect(() => {
    const div = document.createElement("div");

    div.style.overflowY = "scroll";
    div.style.width = "50px";
    div.style.height = "50px";

    // Must insert in document for get real size
    document.body.append(div);
    const scrollWidth = div.offsetWidth - div.clientWidth;
    div.remove();

    setScrollWidth(scrollWidth);
  }, []);

  const isMobile = useIsMobile();

  return (
    <header
      css={{
        position: "sticky",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: uiColors.backgroundMinor,
        boxSizing: "border-box",
        ...(isMobile
          ? {
              width: `calc(100vw - ${scrollWidth}px)`,
              height: `${headerMobileHeight}px`,
              insetInlineStart: "0px",
              gridTemplateColumns: "min-content auto",
              paddingInlineEnd: "12px",
            }
          : {
              width: `calc(100vw - ${navMenuWidth}px - ${scrollWidth}px)`,
              height: `${headerDesktopHeight}px`,
              insetInlineStart: `${navMenuWidth}px`,
              gridTemplateColumns: "auto",

              paddingInlineStart: "16px",
              paddingInlineEnd: "12px",
              paddingBlockStart: "12px",
              paddingBlockEnd: "8px",
            }),
        ...(stickyHeader && {
          top: "0",
          zIndex: "1",
        }),
      }}
    >
      {children}
    </header>
  );
};

const Title = ({
  title,
  hiddenTitle,
}:
  | { title: ReactNode; hiddenTitle?: never }
  | {
      title?: never;
      hiddenTitle: string;
    }) => {
  const typo = useTypo();
  const uiColors = useUiColors();
  const isMobile = useIsMobile();

  return (
    <h1
      css={{
        ...(hiddenTitle && { visibility: "hidden" }),
        ...cssFns.margin("0"),
        marginInlineEnd: "16px",
        color: uiColors.text,
        ...(isMobile
          ? typo({
              level: "body1",
              weight: "medium",
              density: "tight",
            })
          : typo({
              level: "title3",
              weight: "medium",
              density: "tight",
            })),
      }}
    >
      {title ?? hiddenTitle}
    </h1>
  );
};

const BottomSheetPortal = forwardRef<HTMLDivElement, {}>(({}, ref) => {
  const isMobile = useIsMobile();

  const [scrollWidth, setScrollWidth] = useState(0);

  useBrowserLayoutEffect(() => {
    const div = document.createElement("div");

    div.style.overflowY = "scroll";
    div.style.width = "50px";
    div.style.height = "50px";

    // Must insert in document for get real size
    document.body.append(div);
    const scrollWidth = div.offsetWidth - div.clientWidth;
    div.remove();

    setScrollWidth(scrollWidth);
  }, []);

  return (
    <div
      ref={ref}
      css={{
        position: "sticky",
        bottom: "0",
        boxSizing: "border-box",
        ...(isMobile
          ? {
              width: "100vw",
              insetInlineStart: "0",
            }
          : {
              width: `calc(100vw - ${navMenuWidth}px - ${scrollWidth}px)`,
              insetInlineStart: `${navMenuWidth}px`,
            }),
      }}
    />
  );
});
